import { BordBadge } from 'bord-design-system';
import QuoteDetailBanner from '../QuoteDetailBanner/QuoteDetailBanner';
import { bannerVariant, QUOTE_STATUS_CODE } from 'types/quoteDetails.types';
import QuoteDetailNoticeSkeleton from './components/QuoteDetailNoticeSkeleton/QuoteDetailNoticeSkeleton';
import { EQuoteSlaPolicyCode, EQuoteSlaType, IQuoteDetailSla, IQuoteItem } from 'types/quote.types';
import { PRODUCT_CATEGORY } from 'types/category';

import './QuoteDetailNotice.scss';

interface IQuoteDetailNotice {
  isLoading?: boolean;
  slas?: IQuoteDetailSla[];
  slaElapsedHours?: number;
  quoteStatusCode?: QUOTE_STATUS_CODE;
  items?: IQuoteItem[];
  isExploratory?: boolean;
}

/** Horas del SLA de procurement cuando la cotización incluye ítems CTO. */
const CTO_SLA_HOURS = 72;

/** Horas del SLA de procurement para cotizaciones exploratorias sin datos del backend. */
const EXPLORATORY_PROCUREMENT_SLA_HOURS = 24;

/** Estados en los que aplica el SLA de procurement. */
const PROCUREMENT_QUOTE_STATUS_CODES: QUOTE_STATUS_CODE[] = [
  QUOTE_STATUS_CODE.PENDING,
  QUOTE_STATUS_CODE.TO_QUOTE
];

/** Mapa de estados activos al tipo de SLA que aplica en cada uno. */
const STATUS_TO_SLA_TYPE: Partial<Record<QUOTE_STATUS_CODE, EQuoteSlaType>> = {
  [QUOTE_STATUS_CODE.PENDING]: EQuoteSlaType.PROCUREMENT_RESPONSE_TIME,
  [QUOTE_STATUS_CODE.TO_QUOTE]: EQuoteSlaType.PROCUREMENT_RESPONSE_TIME,
  [QUOTE_STATUS_CODE.PENDING_APPROVAL]: EQuoteSlaType.CLIENT_RESPONSE_TIME,
  [QUOTE_STATUS_CODE.PARTIAL_APPROVED]: EQuoteSlaType.CLIENT_RESPONSE_TIME
};

/**
 * Aviso informativo del detalle de cotización usando QuoteDetailBanner.
 * El SLA relevante se elige según el estado de la cotización: procurement
 * en PENDING/TO_QUOTE y cliente en PENDING_APPROVAL/PARTIAL_APPROVED.
 * Estados sin SLA accionable (terminales o APPROVED) ocultan el banner.
 * El banner también se oculta cuando el SLA de procurement está vencido,
 * porque la cotización sigue siendo accionable y mostrarla como "vencida"
 * sería engañoso (sí se muestra "Cotización vencida" cuando el SLA del
 * cliente está vencido, ya que ahí la ventana de aprobación se perdió).
 * Cuando la cotización es CTO (política del SLA en CTO o algún ítem de categoría
 * CTO) el SLA de procurement es de 72 horas y se agrega el badge "Con CTO".
 * Si el backend aún no refleja las 72 horas en el SLA, se aplica un fallback:
 * se muestran 72 horas y la variante se calcula con las horas transcurridas.
 * Las cotizaciones exploratorias no traen SLA del backend: se usa un fallback
 * fijo de 24 horas de procurement con las horas transcurridas del API.
 */
const QuoteDetailNotice = ({
  isLoading,
  slas,
  slaElapsedHours,
  quoteStatusCode,
  items,
  isExploratory
}: IQuoteDetailNotice) => {
  const relevantSlaType = quoteStatusCode ? STATUS_TO_SLA_TYPE[quoteStatusCode] : undefined;
  const relevantSla =
    !isExploratory && relevantSlaType
      ? slas?.find(sla => sla?.quoteSlaType?.code === relevantSlaType)
      : undefined;
  const isClientSla = relevantSlaType === EQuoteSlaType.CLIENT_RESPONSE_TIME;
  const isProcurementStatus =
    PROCUREMENT_QUOTE_STATUS_CODES.includes(quoteStatusCode as QUOTE_STATUS_CODE) || !quoteStatusCode;
  const useExploratoryFallback = !!isExploratory && isProcurementStatus;
  const hasActiveSla = !!relevantSla || useExploratoryFallback;
  const elapsedHours = slaElapsedHours ?? 0;
  const hasCtoItems = items?.some(item => item?.categoryName === PRODUCT_CATEGORY.CTO_LAPTOPS) ?? false;
  const isCtoSla =
    !isClientSla && !useExploratoryFallback && (relevantSla?.quoteSlaPolicy?.code === EQuoteSlaPolicyCode.CTO || hasCtoItems);
  const usesCtoFallback = isCtoSla && (relevantSla?.hours ?? 0) < CTO_SLA_HOURS;
  const displayHours = useExploratoryFallback
    ? EXPLORATORY_PROCUREMENT_SLA_HOURS
    : usesCtoFallback
    ? CTO_SLA_HOURS
    : relevantSla?.hours;
  const actualDate = new Date();

  /**
   * Determina la variante del banner según el estado del SLA relevante.
   * Con el fallback CTO activo las fechas del SLA corresponden a 24 horas,
   * por lo que la variante se calcula con las horas transcurridas contra 72.
   * Con el fallback exploratorio se calcula contra 24 horas fijas.
   */
  const getSlaVariant = (): bannerVariant => {
    if (useExploratoryFallback) {
      if (elapsedHours >= EXPLORATORY_PROCUREMENT_SLA_HOURS) return bannerVariant.danger;
      if (elapsedHours >= EXPLORATORY_PROCUREMENT_SLA_HOURS * 0.7) return bannerVariant.warning;
      return bannerVariant.information;
    }
    if (!relevantSla) return bannerVariant.information;
    if (usesCtoFallback) {
      const elapsedHours = slaElapsedHours ?? 0;
      if (elapsedHours >= CTO_SLA_HOURS) return bannerVariant.danger;
      if (elapsedHours >= CTO_SLA_HOURS * 0.7) return bannerVariant.warning;
      return bannerVariant.information;
    }
    if (actualDate > new Date(relevantSla.expiresAt)) return bannerVariant.danger;
    if (actualDate > new Date(relevantSla.seventyPercentAt)) return bannerVariant.warning;
    return bannerVariant.information;
  };

  /** Formatea la fecha de expiración del cliente: "mié. 18 mar · 3:00 PM" */
  const formatClientDate = (isoDate: string) => {
    const date = new Date(isoDate);
    const weekday = date.toLocaleDateString('es', { weekday: 'short' });
    const day = date.getDate();
    const month = date.toLocaleDateString('es', { month: 'short' });
    const time = date.toLocaleTimeString('es', { hour: 'numeric', minute: '2-digit', hour12: true });
    return `${weekday} ${day} ${month} · ${time}`;
  };

  /** Formatea la fecha como DD/MM/YYYY */
  const formatShortDate = (isoDate: string) => {
    const date = new Date(isoDate);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const slaVariant = getSlaVariant();
  const isDanger = slaVariant === bannerVariant.danger;

  const labelColorMap: Record<bannerVariant, string> = {
    [bannerVariant.information]: '#4096F0',
    [bannerVariant.warning]: '#E29E28',
    [bannerVariant.danger]: '#DE072E'
  };

  /** Construye el mensaje del banner según el SLA relevante y la variante. */
  const buildDisplayMessage = () => {
    if (!hasActiveSla) return null;
    if (isClientSla && relevantSla) {
      return isDanger
        ? <div>Cotización vencida No concretada: {formatShortDate(relevantSla.expiresAt)}</div>
        : <div>El cliente tiene hasta el <strong>{formatClientDate(relevantSla.expiresAt)}</strong> para aprobar</div>;
    }
    return (
      <div>
        <span style={{ color: labelColorMap[slaVariant] }}>SLA de la cotización:</span> {displayHours} horas –{' '}
        <strong>{elapsedHours} horas transcurridas</strong>
        {isCtoSla && <BordBadge className="ctoBadge" size="small" variant="outlined" label="Con CTO" />}
      </div>
    );
  };

  const displayMessage = buildDisplayMessage();
  const hideBanner = !hasActiveSla || (!isClientSla && isDanger && !useExploratoryFallback);

  if (isLoading) return <QuoteDetailNoticeSkeleton />;
  if (hideBanner) return null;
  return (
    <div className="quoteDetailNotice">
      <QuoteDetailBanner variant={slaVariant} content={displayMessage} />
    </div>
  );
};

export default QuoteDetailNotice;
